<template>
  <ion-page>
    <main-header>
      <div v-if="userIsVendor" class="ion-text-end" slot="end">
        <ion-button @click="navigateToEditMenu()">Edit Menu</ion-button>
      </div>
    </main-header>
    <ion-content padding>
      <get-the-app v-if="!userIsAdmin && !userIsVendor && !userIsRunner" />

      <div v-else>
        <loading-spinner v-if="loading"></loading-spinner>

        <div v-else>
          <ion-grid
            class="ion-no-padding location-picking-grid"
            v-if="locations.length"
          >
            <ion-row>
              <ion-col size="12" size-md="8" class="ion-no-padding">
                <h2 v-if="!location">Choose a Location</h2>
                <h2 v-else>
                  <ion-avatar>
                    <img :src="location.photoURL" alt="photo for location" />
                  </ion-avatar>
                  {{ location.name }}
                </h2>
              </ion-col>
              <ion-col size="12" size-md="4" class="ion-no-padding">
                <ion-list class="ion-no-padding">
                  <ion-item>
                    <ion-label>Location</ion-label>
                    <ion-select
                      placeholder="Choose"
                      :value="locationId"
                      @ionChange="setCurrentLocation($event.detail.value)"
                      data-cy="choose-location-dropdown"
                    >
                      <ion-select-option
                        v-for="availableLocation in locations"
                        :key="`available-location-${availableLocation.id}`"
                        :value="availableLocation.id"
                      >
                        {{ availableLocation.name }}
                      </ion-select-option>
                    </ion-select>
                  </ion-item>
                </ion-list>
              </ion-col>
            </ion-row>
          </ion-grid>
          <div
            v-else
            class="background-color ion-color-warning ion-padding ion-text-center"
          >
            There are no locations available.
          </div>

          <div v-if="location">
            <loading-spinner v-if="loadingOpenOrders"></loading-spinner>
            <div v-else>
              <ion-card v-if="!openOrders.length" color="primary">
                <ion-card-header>
                  <ion-card-title class="ion-text-center">
                    There are no open orders...
                  </ion-card-title>
                </ion-card-header>
                <ion-card-content class="ion-text-center">
                  Go get yourself a snack, walk around, show off the food...
                </ion-card-content>
              </ion-card>

              <div v-else>
                <h3>Open / Pending Orders</h3>
                <ion-grid class="order-table">
                  <ion-row class="header">
                    <ion-col
                      v-if="hasMultipleFulfillments"
                      size="1"
                      class="fulfillment-icon-header"
                    >
                    </ion-col>
                    <ion-col size="2">
                      Customer / Order ID
                    </ion-col>
                    <ion-col size="2">
                      Items
                    </ion-col>
                    <ion-col size="2">
                      Location
                    </ion-col>
                    <ion-col size="2">
                      Placed
                    </ion-col>
                    <ion-col size="2">
                      Status
                    </ion-col>
                    <ion-col :size="hasMultipleFulfillments ? 1 : 2"> </ion-col>
                  </ion-row>
                  <ion-row
                    v-for="order in openOrders"
                    :key="`order-${order.id}`"
                    :class="{
                      'fulfillment-delivery':
                        hasMultipleFulfillments && order.isDelivery(),
                      'fulfillment-pickup':
                        hasMultipleFulfillments && !order.isDelivery()
                    }"
                  >
                    <ion-col
                      v-if="hasMultipleFulfillments"
                      size="1"
                      class="fulfillment-icon"
                    >
                      <ion-icon
                        v-if="order.isDelivery()"
                        name="walk"
                      ></ion-icon>
                      <ion-icon v-else name="basket"></ion-icon>
                    </ion-col>
                    <ion-col size="2">
                      <a
                        v-if="order.status !== ORDER_OPEN"
                        href="#"
                        @click.prevent="showOrderDetails(order)"
                      >
                        {{ order.displayName }}
                        <br />
                        {{ order.friendlyIdentifier() }}
                      </a>
                      <ion-text v-else>
                        {{ order.displayName }}
                        <br />
                        {{ order.friendlyIdentifier() }}
                      </ion-text>
                    </ion-col>
                    <ion-col size="2">
                      <ul>
                        <li
                          v-for="(item, index) in order.items"
                          :key="`order-${order.id}-item-${index}`"
                        >
                          {{ item.quantity }} {{ item.name }}
                        </li>
                      </ul>
                      <ion-button
                        v-if="order.status !== ORDER_OPEN"
                        expand="block"
                        size="small"
                        color="dark"
                        @click="showOrderDetails(order)"
                      >
                        Details
                      </ion-button>
                    </ion-col>
                    <ion-col size="2">
                      <ion-text v-if="order.isDelivery()">
                        Section {{ order.section }}, Row {{ order.row }}, Seat
                        {{ order.seat }}
                      </ion-text>
                      <ion-text v-else>
                        Pick-up
                      </ion-text>
                    </ion-col>
                    <ion-col size="1">
                      <strong>{{ order.requested | orderTimeDisplay }}</strong>
                      <br />
                      {{ order.requested | orderDateDisplay }}
                    </ion-col>
                    <ion-col size="2">
                      <ion-badge
                        v-if="order.status == ORDER_OPEN"
                        color="primary"
                      >
                        Pending
                      </ion-badge>
                      <template v-if="order.status == ORDER_PREPARING">
                        <ion-badge color="tertiary">
                          Preparing
                        </ion-badge>
                        <div class="user-responsible-display">
                          <ion-avatar>
                            <img :src="getUserAvatar(order.preparingUserId)" />
                          </ion-avatar>
                          {{ getUserDisplayName(order.preparingUserId) }}
                        </div>
                      </template>
                      <template v-if="order.status == ORDER_DELIVERING">
                        <ion-badge color="tertiary">
                          Delivering
                        </ion-badge>
                        <div class="user-responsible-display">
                          <ion-avatar>
                            <img :src="getUserAvatar(order.deliveringUserId)" />
                          </ion-avatar>
                          {{ getUserDisplayName(order.deliveringUserId) }}
                        </div>
                      </template>
                      <template v-if="order.status == ORDER_READY">
                        <ion-badge color="tertiary">
                          Ready for Pick-up
                        </ion-badge>
                      </template>
                    </ion-col>
                    <ion-col :size="hasMultipleFulfillments ? 2 : 3">
                      <ion-button
                        v-if="order.status == ORDER_OPEN"
                        @click="confirm(order)"
                      >
                        Start
                      </ion-button>
                      <ion-button
                        v-if="
                          order.status == ORDER_PREPARING && order.isDelivery()
                        "
                        @click="deliver(order)"
                      >
                        Deliver
                      </ion-button>
                      <ion-button
                        v-if="
                          order.status == ORDER_PREPARING && !order.isDelivery()
                        "
                        @click="ready(order)"
                      >
                        Ready
                      </ion-button>
                      <ion-button
                        v-if="order.status == ORDER_DELIVERING"
                        @click="complete(order)"
                      >
                        Mark Delivered
                      </ion-button>
                      <ion-button
                        v-if="order.status == ORDER_READY"
                        @click="complete(order)"
                      >
                        Collected
                      </ion-button>
                    </ion-col>
                  </ion-row>
                </ion-grid>
              </div>
            </div>

            <hr />

            <template v-if="recentlyCompletedOrders.length">
              <div>
                <h3>Recently Completed Orders</h3>
                <ion-grid class="order-table">
                  <ion-row class="header">
                    <ion-col size="2">
                      Order ID
                    </ion-col>
                    <ion-col size="2">
                      Items
                    </ion-col>
                    <ion-col size="2">
                      Location
                    </ion-col>
                    <ion-col size="6">
                      Completed
                    </ion-col>
                  </ion-row>
                  <ion-row
                    v-for="order in recentlyCompletedOrders"
                    :key="`order-${order.id}`"
                  >
                    <ion-col size="2">
                      {{ order.friendlyIdentifier() }}
                    </ion-col>
                    <ion-col size="2">
                      <ul>
                        <li
                          v-for="(item, index) in order.items"
                          :key="`order-${order.id}-item-${index}`"
                        >
                          {{ item.quantity }} {{ item.name }}
                        </li>
                      </ul>
                      <ion-button
                        expand="block"
                        size="small"
                        color="dark"
                        @click="showOrderDetails(order)"
                      >
                        Details
                      </ion-button>
                    </ion-col>
                    <ion-col size="2">
                      <ion-text v-if="order.isDelivery()">
                        Section {{ order.section }}, Row {{ order.row }}, Seat
                        {{ order.seat }}
                      </ion-text>
                      <ion-text v-else>
                        Pick-up
                      </ion-text>
                    </ion-col>
                    <ion-col size="6">
                      {{
                        order.completedAt
                          ? order.completedAt.toLocaleString()
                          : ""
                      }}
                    </ion-col>
                  </ion-row>
                </ion-grid>
              </div>
            </template>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
/**
 * This is a pretty complex component.  There are a few like this throughout the project
 * that looking back, I'd like to have made into smaller components.  Perhaps during a refactor
 * that might be a good idea. For time constraints, I've unfortunately going to need to keep
 * this a monolith component
 */

import {
  ORDER_OPEN,
  ORDER_PREPARING,
  ORDER_DELIVERING,
  ORDER_READY,
  ORDER_COMPLETED
} from "@/constants/order";

import LocationModelService from "@/services/LocationModel";
import UserModelService from "@/services/UserModel";
import { mapGetters, mapMutations } from "vuex";
import GetTheApp from "@/components/Shared/GetTheApp/GetTheApp";
import Order from "@/models/Order";
import Location from "@/models/Location";
import errorAlert from "@/ErrorAlert";
import OrderStartModal from "@/components/Web/OrderStartModal";
import OrderDetailsModal from "@/components/Web/OrderDetailsModal";

let openOrdersListener = () => {};
let recentlyCompletedOrdersListener = () => {};

export default {
  title: "Order Dashboard",

  components: {
    "get-the-app": GetTheApp
  },

  data: function() {
    return {
      ORDER_OPEN,
      ORDER_PREPARING,
      ORDER_DELIVERING,
      ORDER_READY,
      locations: [],
      locationId: null,
      loading: true,
      loadingOpenOrders: true,
      openOrders: [],
      recentlyCompletedOrders: [],
      usersCache: {}
    };
  },

  /**
   * If you navigate to yourself, using a link in the menu, it wants to unset the param. This handles that case
   */
  beforeRouteUpdate(to, from, next) {
    if (!to.params.location) {
      return next({
        name: "Web: Order Dashboard",
        params: {
          location: this.locationId
        }
      });
    }

    return next();
  },

  /**
   * Make sure to unsubscribe from any listeners
   */
  beforeRouteLeave(to, from, next) {
    openOrdersListener();
    recentlyCompletedOrdersListener();
    next();
  },

  async created() {
    if (this.userIsAdmin || this.userIsRunner || this.userIsVendor) {
      let searchForLocationId =
        this.$route.params.location ||
        (this.$store.state.processor.location &&
          this.$store.state.processor.location.id);

      let q = LocationModelService.currentUserLocations();
      q.then(locations => {
        locations.data.forEach(location => {
          location = Location.firebaseConverter.fromFirestore({
            id: location.id,
            data: () => {
              return { ...location };
            }
          });

          if (location.id === searchForLocationId) {
            this.setCurrentLocation(location.id);
          }
          this.locations.push(location);
        });

        if (!this.locationId && searchForLocationId) {
          errorAlert("Unable to find location");
        }

        if (!this.locationId && this.locations.length > 0) {
          this.setCurrentLocation(this.locations[0].id);
        }
      })
        .catch(() => {
          errorAlert("Unable to load locations");
        })
        .finally(() => {
          // next tick is to keep from flashing on the computed location
          this.$nextTick(() => {
            this.loading = false;
          });
        });
    } else {
      this.loading = false;
    }
  },

  methods: {
    ...mapMutations("processor", ["setProcessorLocation"]),

    setCurrentLocation(locationId) {
      this.locationId = locationId;
      this.$nextTick(() => {
        this.setProcessorLocation(this.location);
      });

      if (this.$route.params.location != locationId) {
        this.$router.push({
          name: "Web: Order Dashboard",
          params: {
            location: locationId
          }
        });
      }
    },

    showOrderDetails(order) {
      this.$ionic.modalController
        .create({
          component: OrderDetailsModal,
          componentProps: {
            propsData: {
              order,
              customerName: order.displayName,
              closeModal: () => {
                this.$ionic.modalController.dismiss();
              }
            }
          }
        })
        .then(m => m.present());
    },

    confirm(order) {
      this.$ionic.modalController
        .create({
          component: OrderStartModal,
          componentProps: {
            propsData: {
              order,
              location: this.location,
              closeModal: () => {
                this.$ionic.modalController.dismiss();
              },
              startOrder: () => {
                this.startOrder(order);
                this.$ionic.modalController.dismiss();
                this.showOrderDetails(order);
              }
            }
          }
        })
        .then(m => m.present());
    },

    startOrder(order) {
      return this.$firestore()
        .collection("orders")
        .doc(order.id)
        .update({
          status: ORDER_PREPARING,
          preparingUserId: this.user.uid,
          preparingStartAt: this.$firestore.FieldValue.serverTimestamp()
        });
    },

    deliver(order) {
      return this.$firestore()
        .collection("orders")
        .doc(order.id)
        .update({
          status: ORDER_DELIVERING,
          deliveringUserId: this.user.uid,
          deliveringStartAt: this.$firestore.FieldValue.serverTimestamp()
        });
    },

    ready(order) {
      return this.$firestore()
        .collection("orders")
        .doc(order.id)
        .update({
          status: ORDER_READY,
          readyUserId: this.user.uid,
          readyStartAt: this.$firestore.FieldValue.serverTimestamp()
        });
    },

    complete(order) {
      return this.$firestore()
        .collection("orders")
        .doc(order.id)
        .update({
          status: ORDER_COMPLETED,
          completedUserId: this.user.uid,
          completedAt: this.$firestore.FieldValue.serverTimestamp()
        });
    },

    getUserAvatar(userId) {
      return this.usersCache[userId] ? this.usersCache[userId].photoURL : "";
    },

    getUserDisplayName(userId) {
      return this.usersCache[userId] ? this.usersCache[userId].displayName : "";
    },

    navigateToEditMenu() {
      this.$router
        .push({
          name: "Web: Location Menu Edit",
          params: {
            id: this.locationId
          }
        })
        .catch(e => e);
    }
  },

  computed: {
    ...mapGetters("processor", ["processorLocation"]),
    ...mapGetters("auth", [
      "user",
      "userIsAdmin",
      "userIsVendor",
      "userIsRunner"
    ]),

    location() {
      return this.locations.find(location => location.id == this.locationId);
    },

    hasMultipleFulfillments() {
      return this.location.hasMultipleFulfillments();
    }
  },

  watch: {
    locationId(newValue) {
      openOrdersListener();
      recentlyCompletedOrdersListener();

      this.loadingOpenOrders = true;
      openOrdersListener = this.$firestore()
        .collection("orders")
        .where("locationId", "==", newValue)
        .where("status", "in", [
          ORDER_OPEN,
          ORDER_PREPARING,
          ORDER_READY,
          ORDER_DELIVERING
        ])
        .withConverter(Order.firebaseConverter)
        .onSnapshot(
          async ordersSnapshot => {
            let newOpenOrders = [];
            let userIds = [];

            ordersSnapshot.forEach(orderRef => {
              let order = orderRef.data();
              newOpenOrders.push(order);

              if (
                order.preparingUserId &&
                !this.usersCache[order.preparingUserId]
              ) {
                userIds.push(order.preparingUserId);
              }
              if (
                order.deliveringUserId &&
                !this.usersCache[order.deliveringUserId]
              ) {
                userIds.push(order.deliveringUserId);
              }
            });

            // sort by requested here instead of with query
            // because when sorting on query, somehow the snapshots don't update the vue display
            // I'm honestly not sure why because it seems like orderby works on the one below this
            newOpenOrders.sort((a, b) => {
              return a.requested - b.requested;
            });

            if (userIds.length) {
              let newlyFoundUsers = await UserModelService.getUsersByIdsKeyedById(
                userIds
              );
              this.usersCache = { ...this.usersCache, ...newlyFoundUsers };
            }

            this.openOrders = newOpenOrders;
            this.loadingOpenOrders = false;
          },
          () => {
            this.loadingOpenOrders = false;
          }
        );

      // yeah this section looks very similar - should maybe be done better?
      const oldestOrderTimestamp = new Date();
      oldestOrderTimestamp.setHours(oldestOrderTimestamp.getHours() - 8);

      recentlyCompletedOrdersListener = this.$firestore()
        .collection("orders")
        .where("locationId", "==", newValue)
        .where("status", "==", ORDER_COMPLETED)
        .where("completedAt", ">=", oldestOrderTimestamp)
        .orderBy("completedAt", "desc")
        .withConverter(Order.firebaseConverter)
        .onSnapshot(async ordersSnapshot => {
          this.recentlyCompletedOrders = [];
          let userIds = [];

          ordersSnapshot.forEach(orderRef => {
            let order = orderRef.data();
            this.recentlyCompletedOrders.push(order);

            if (
              order.preparingUserId &&
              !this.usersCache[order.preparingUserId]
            ) {
              userIds.push(order.preparingUserId);
            }
            if (
              order.deliveringUserId &&
              !this.usersCache[order.deliveringUserId]
            ) {
              userIds.push(order.deliveringUserId);
            }
          });

          if (userIds.length) {
            let newlyFoundUsers = await UserModelService.getUsersByIdsKeyedById(
              userIds
            );
            this.usersCache = { ...this.usersCache, ...newlyFoundUsers };
          }
        });
    }
  },

  filters: {
    orderDateDisplay(date) {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ];

      let d = new Date(date);

      return months[d.getMonth()] + " " + d.getDate();
    },

    orderTimeDisplay(date) {
      let d = new Date(date);
      return d.toLocaleTimeString();
    }
  }
};
</script>

<style lang="scss">
.location-picking-grid {
  h2 {
    margin-top: 12px;
  }
  ion-avatar {
    width: 1.8rem;
    height: 1.8rem;
    display: inline-block;
    vertical-align: text-bottom;
  }
}

.user-responsible-display {
  ion-avatar {
    height: 32px;
    width: 32px;
    display: inline-block;
    vertical-align: middle;
  }
}

.fulfillment-icon {
  text-align: center;
  ion-icon {
    font-size: 34px;
  }
}
.fulfillment-delivery {
  background: #ddedff;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 70%, #cdedff 100%);

  .fulfillment-icon {
    border-left: 5px solid #84b1ff !important;
    ion-icon {
      color: #3366bb;
    }
  }
}
.fulfillment-pickup {
  background: #ffeedd;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 70%, #bbffbc 100%);

  .fulfillment-icon {
    border-left: 5px solid #71e08d !important;
    ion-icon {
      color: #33bb44;
    }
  }
}
</style>
